/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type ActionCloseArgs, type BaseRequestOptionsArgs, type Command, type HttpResponse, BatchService, HttpService, PromptService, Status } from '@dm/core';
import { type ServiceLDAP } from 'app/common/types/mo';
import { type Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ServiceLdapStrService } from './service-ldap-str.service';

@Injectable({ providedIn: 'root' })
export class ServiceLdapService {
    constructor(
        private http: HttpService,
        private batchService: BatchService,
        private promptService: PromptService,
        private str: ServiceLdapStrService,
    ) {}

    /**
     * 适配从接口查询回来的数据
     * @param data
     */
    adaptData(data: ServiceLDAP): void {
        if (data.LDAPSERVER) {
            const ips = data.LDAPSERVER.split(',');
            data.primaryIp = ips[0];
            data.standbyIp1 = ips[1];
            data.standbyIp2 = ips[2];
        }
    }

    /**
     * 查询基础设置
     */
    get(vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('serviceLdap.get');
        rest.setParams({
            vstoreId,
        });
        return this.http.get(rest, options)
            .pipe(
                tap(({ data }) => {
                    this.adaptData(data);
                }),
            );
    }

    /**
     * 查询高级设置
     * @param templateType 模板类型
     */
    getAdvanced(vstoreId: string, templateType: number, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('serviceLdap.getAdvanced');
        rest.setParams({
            vstoreId,
        });

        if (templateType) {
            rest.setParams({
                SCHEMATYPE: templateType,
            });
        }

        return this.http.get(rest, options);
    }

    /**
     * 修改基础设置
     */
    modify(vstoreId: string, oriParams: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        let params = oriParams;
        const rest = this.http.makeUrl('serviceLdap.put');
        params = {
            ...params,
            vstoreId,
        };
        return this.http.put(rest, params, options);
    }

    /**
     * 修改高级设置
     */
    modifyAdvanced(vstoreId: string, oriParams: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        let params = oriParams;
        const rest = this.http.makeUrl('serviceLdap.putAdvanced');
        params = {
            ...params,
            vstoreId,
        };
        return this.http.put(rest, params, options);
    }

    /**
     * 初始化配置
     */
    delete(vstoreId: string, oriOptions?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        let options = oriOptions;
        const rest = this.http.makeUrl('serviceLdap.delete');
        options = {
            ...options,
            body: {
                vstoreId,
            },
        };
        return this.http.delete(rest, options);
    }

    /**
     * 初始化配置
     */
    deleteAdvanced(vstoreId: string, oriOptions?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        let options = oriOptions;
        const rest = this.http.makeUrl('serviceLdap.deleteAdvanced');
        options = {
            ...options,
            body: {
                vstoreId,
            },
        };
        return this.http.delete(rest, options);
    }

    /**
     * 恢复初始配置
     */
    reset(vstoreId: string, actionClose: (args: ActionCloseArgs) => void): void {
        this.promptService.warning({
            id: 'prompt-service-ldap-reset',
            title: this.str.i18n.resetBtn,
            content: this.str.i18n.resetMsg,
            onOk: () => {
                this.promptService.close();
                const cmds: Command[] = [
                    {
                        name: this.str.i18n.deleteResult,
                        action: this.delete(vstoreId),
                    },
                    {
                        name: this.str.i18n.deleteAdvancedResult,
                        action: this.deleteAdvanced(vstoreId),
                    },
                ];
                this.batchService.exePrompt(cmds, {
                    onClose: (results) => {
                        actionClose({ refresh: results.some((a) => a.status === Status.success) });
                    },
                });
            },
        });
    }

    /**
     * 测试服务器地址
     */
    test(vstoreId: string, address: string, portNum: number, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('serviceLdap.test');
        const params = {
            vstoreId,
            LDAPSERVER: address,
            PORTNUM: portNum,
        };
        return this.http.put(rest, params, options);
    }
}
